import React, { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import { FormControlLabel, IconButton, RadioGroup, Radio } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import moment from 'moment';

interface EditSessionDateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (startNow: boolean, scheduledDate: Date | null) => void;
  scheduledDate: Date | null;
  isEditingEndDate?: boolean;
  activeSessionStartDate?: Date | null;
  scheduledStartDate?: Date | null;
}

const IMMEDIATE = "startNow";
const SCHEDULED = "schedule";
const DATE_TIME_LOCAL_FORMAT = "YYYY-MM-DDTHH:mm";

const EditSessionDateModal: React.FunctionComponent<EditSessionDateModalProps> = ({ isOpen, onClose, onConfirm, scheduledDate, isEditingEndDate, activeSessionStartDate, scheduledStartDate }) => {
  
  // set initial state
  const [immediateOrScheduled, setImmediateOrScheduled] = useState(scheduledDate ? SCHEDULED : IMMEDIATE);
  const [userScheduledDate, setUserScheduledDate] = useState<Date | null>(scheduledDate);
  const [error, setError] = useState<string | null>(null);

  React.useEffect(() => {
    // update state when props change
    setImmediateOrScheduled(scheduledDate ? SCHEDULED : IMMEDIATE);
    setUserScheduledDate(scheduledDate);
    setError(null);
  }, [scheduledDate])

  const handleClose = () => {
    onClose();
    // restore defaults
    setImmediateOrScheduled(IMMEDIATE);
    setUserScheduledDate(new Date());
    setError(null);
  };

  const handleConfirm = () => {
    const now = new Date();

    if (immediateOrScheduled !== IMMEDIATE && isEditingEndDate && userScheduledDate) {
      if (isEditingEndDate && scheduledStartDate && userScheduledDate <= scheduledStartDate) {
        setError('End date must be after the scheduled start date.');
        return;
      }
      
      if (activeSessionStartDate && userScheduledDate <= activeSessionStartDate) {
        setError('End date must be after the active session start date.');
        return;
      }

      if (userScheduledDate <= now) {
        setError('Scheduled end date must be in the future.');
        return;
      }
    }
    else if(immediateOrScheduled == IMMEDIATE && isEditingEndDate) // If user selected "End Now"
    {
      // if scheduled start date is before end now date, show error
      if (isEditingEndDate && scheduledStartDate && scheduledStartDate >= now) {
        setError('Cannot end session that has not started.');
        return;
      }
    }

    onConfirm(immediateOrScheduled === IMMEDIATE, userScheduledDate);
    setImmediateOrScheduled(IMMEDIATE);
    setUserScheduledDate(new Date());
  };

  const handleStartOptionsChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
    setImmediateOrScheduled((event.target as HTMLInputElement).value);
    setError(null);
  }

  return (
    <Dialog open={isOpen} onClose={handleClose}>
      <DialogTitle>
        {isEditingEndDate ? 'Schedule End Date' : 'Schedule Start Date'}
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent sx={{ paddingTop: 0, width: "340px" }}>
        <RadioGroup
          value={immediateOrScheduled}
          onChange={handleStartOptionsChanged}
        >
          {isEditingEndDate ? (
          <FormControlLabel value={IMMEDIATE} control={<Radio />} label="End Now" />
          ) : (
            <FormControlLabel value={IMMEDIATE} control={<Radio />} label="Start Now" />
          )}
          <FormControlLabel
            value={SCHEDULED}
            control={<Radio />}
            label={isEditingEndDate ? 'Schedule End' : 'Schedule Start'}
          />
        </RadioGroup>
        <TextField
          label={isEditingEndDate ? 'Schedule End' : 'Schedule Start'}
          type="datetime-local"
          variant="standard"          
          value={!!userScheduledDate ? moment(userScheduledDate).format(DATE_TIME_LOCAL_FORMAT) : moment(new Date()).format(DATE_TIME_LOCAL_FORMAT)} // use datetime-local compatible format
          disabled={immediateOrScheduled === IMMEDIATE}
          onChange={(e) => {
            setUserScheduledDate(moment(e.target.value, DATE_TIME_LOCAL_FORMAT).toDate());
            setError(null);
          }}
          fullWidth
          margin="dense"
        />
        {error && ( // Render error message if error exists
          <Typography color="error" variant="body2" sx={{ marginTop: 1 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>CANCEL</Button>
        <Button type="submit" onClick={handleConfirm}>CONFIRM</Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditSessionDateModal;
